(() => {
  const STORAGE_KEY = 'adawat_wishlist_v1';

  const read = () => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  };

  const write = (items) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    document.dispatchEvent(new CustomEvent('adawat:wishlist:change', { detail: { items } }));
  };

  const normalize = (item) => ({
    id: String(item.id || ''),
    handle: item.handle || '',
    title: item.title || '',
    price: item.price || '',
    image: item.image || '',
    url: item.url || '#',
    variantId: item.variantId ? String(item.variantId) : '',
  });

  const AdawatWishlist = {
    get() {
      return read();
    },

    count() {
      return read().length;
    },

    has(id) {
      const key = String(id || '');
      return read().some((item) => item.id === key);
    },

    add(item) {
      const next = normalize(item);
      if (!next.id) return read();
      const items = read().filter((entry) => entry.id !== next.id);
      items.unshift(next);
      write(items);
      return items;
    },

    remove(id) {
      const key = String(id || '');
      const items = read().filter((entry) => entry.id !== key);
      write(items);
      return items;
    },

    toggle(item) {
      const next = normalize(item);
      if (!next.id) return { items: read(), added: false };
      if (this.has(next.id)) {
        return { items: this.remove(next.id), added: false };
      }
      return { items: this.add(next), added: true };
    },

    open() {
      const drawer = document.querySelector('[data-adawat-wishlist-drawer]');
      if (!drawer) return;
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('adawat-wishlist-open');
      this.render();
    },

    close() {
      const drawer = document.querySelector('[data-adawat-wishlist-drawer]');
      if (!drawer) return;
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('adawat-wishlist-open');
    },

    toggleDrawer() {
      const drawer = document.querySelector('[data-adawat-wishlist-drawer]');
      if (!drawer) return;
      if (drawer.classList.contains('is-open')) this.close();
      else this.open();
    },

    render() {
      const drawer = document.querySelector('[data-adawat-wishlist-drawer]');
      if (!drawer) return;

      const items = read();
      const list = drawer.querySelector('[data-wishlist-items]');
      const empty = drawer.querySelector('[data-wishlist-empty]');
      const footer = drawer.querySelector('[data-wishlist-footer]');
      const countLabel = drawer.querySelector('[data-wishlist-count-label]');

      if (countLabel) countLabel.textContent = `(${items.length})`;

      if (!list || !empty) return;

      if (items.length === 0) {
        list.hidden = true;
        list.innerHTML = '';
        empty.hidden = false;
        if (footer) footer.hidden = true;
        return;
      }

      empty.hidden = true;
      list.hidden = false;
      if (footer) footer.hidden = false;
      const closeIcon =
        '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';
      list.innerHTML = items
        .map((item) => {
          const title = escapeHtml(item.title);
          const price = escapeHtml(item.price);
          const url = escapeHtml(item.url || '#');
          const image = escapeHtml(item.image || '');
          const id = escapeHtml(item.id);
          const variantId = escapeHtml(item.variantId || '');
          const thumb = image
            ? `<img src="${image}" alt="${title}" width="88" height="88" loading="lazy">`
            : '';
          const action = variantId
            ? `<button type="button" class="adawat-wishlist-item__cart" data-wishlist-add-cart data-variant-id="${variantId}">Add to cart</button>`
            : `<a href="${url}" class="adawat-wishlist-item__cart">View product</a>`;

          return `
            <article class="adawat-wishlist-item" data-wishlist-id="${id}">
              <a href="${url}" class="adawat-wishlist-item__media">${thumb}</a>
              <div class="adawat-wishlist-item__info">
                <div class="adawat-wishlist-item__title-row">
                  <a href="${url}" class="adawat-wishlist-item__title">${title}</a>
                  <button type="button" class="adawat-wishlist-item__remove" data-wishlist-remove data-product-id="${id}" aria-label="Remove from wishlist">${closeIcon}</button>
                </div>
                ${price ? `<p class="adawat-wishlist-item__price">${price}</p>` : ''}
                ${action}
              </div>
            </article>
          `;
        })
        .join('');
    },
  };

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function productFromButton(button) {
    return {
      id: button.dataset.productId,
      handle: button.dataset.productHandle,
      title: button.dataset.productTitle,
      price: button.dataset.productPrice,
      image: button.dataset.productImage,
      url: button.dataset.productUrl,
      variantId: button.dataset.variantId,
    };
  }

  function syncButtons() {
    document.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
      const active = AdawatWishlist.has(button.dataset.productId);
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      const label = active ? 'Remove from wishlist' : 'Add to wishlist';
      button.setAttribute('aria-label', label);
    });

    document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
      const count = AdawatWishlist.count();
      el.textContent = String(count);
      el.dataset.count = String(count);
      el.hidden = count === 0;
    });
  }

  async function addVariantToCart(variantId) {
    if (window.AdawatCart?.add) {
      await window.AdawatCart.add({ id: Number(variantId), quantity: 1 });
      return;
    }
    const response = await fetch(window.Shopify?.routes?.root ? `${window.Shopify.routes.root}cart/add.js` : '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: Number(variantId), quantity: 1 }),
    });
    if (!response.ok) throw new Error('Could not add to cart');
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    document.dispatchEvent(new CustomEvent('cart:update'));
  }

  function bind() {
    document.addEventListener('click', async (event) => {
      const toggleBtn = event.target.closest('[data-wishlist-toggle]');
      if (toggleBtn) {
        event.preventDefault();
        event.stopPropagation();
        AdawatWishlist.toggle(productFromButton(toggleBtn));
        syncButtons();
        return;
      }

      const openBtn = event.target.closest('[data-wishlist-open]');
      if (openBtn) {
        event.preventDefault();
        AdawatWishlist.open();
        return;
      }

      const closeBtn = event.target.closest('[data-wishlist-close]');
      if (closeBtn) {
        event.preventDefault();
        AdawatWishlist.close();
        return;
      }

      const removeBtn = event.target.closest('[data-wishlist-remove]');
      if (removeBtn) {
        event.preventDefault();
        AdawatWishlist.remove(removeBtn.dataset.productId);
        AdawatWishlist.render();
        syncButtons();
        return;
      }

      const cartBtn = event.target.closest('[data-wishlist-add-cart]');
      if (cartBtn) {
        event.preventDefault();
        const variantId = cartBtn.dataset.variantId;
        if (!variantId) return;
        cartBtn.disabled = true;
        try {
          await addVariantToCart(variantId);
          AdawatWishlist.close();
        } catch (error) {
          cartBtn.disabled = false;
        }
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') AdawatWishlist.close();
    });

    document.addEventListener('adawat:wishlist:change', () => {
      syncButtons();
      const drawer = document.querySelector('[data-adawat-wishlist-drawer]');
      if (drawer?.classList.contains('is-open')) AdawatWishlist.render();
    });

    syncButtons();
  }

  window.AdawatWishlist = AdawatWishlist;
  AdawatWishlist.sync = syncButtons;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind);
  } else {
    bind();
  }
})();
